import React from "react";
import {
  FaCheck,
  FaCheckCircle,
  FaClock,
  FaDumbbell,
  FaExternalLinkAlt,
} from "react-icons/fa";

import {
  buildExerciseKey,
  formatDateFr,
  toBoolean,
} from "../utils/preparationPhysiqueAdherentUtils";

function AdherentExerciseList({
  exercises,
  selectedAdherent,
  validatingKeys = [],
  onValidate,
}) {
  if (!exercises.length) {
    return (
      <div className="rounded-[2rem] border border-dashed border-slate-300 bg-white p-8 text-center shadow-xl shadow-slate-200/50 dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-[1.5rem] bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-white/60">
          <FaDumbbell className="text-2xl" />
        </div>

        <h2 className="mt-5 font-titre text-2xl font-bold text-slate-950 dark:text-white">
          Aucun exercice attribué
        </h2>

        <p className="mx-auto mt-3 max-w-xl text-sm font-medium leading-7 text-slate-500 dark:text-white/50">
          Aucun exercice de préparation physique n'a encore été attribué à ce
          judoka.
        </p>
      </div>
    );
  }

  return (
    <section className="rounded-[2rem] border border-slate-200 bg-white p-4 shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20 sm:p-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-emerald-600 dark:text-emerald-200">
            Programme attribué
          </p>

          <h2 className="mt-2 font-titre text-2xl font-bold text-slate-950 dark:text-white">
            Exercices à réaliser
          </h2>
        </div>

        <p className="text-sm font-medium text-slate-500 dark:text-white/50">
          {exercises.length} exercice{exercises.length > 1 ? "s" : ""}
        </p>
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        {exercises.map((exercise) => {
          const exerciseKey = buildExerciseKey(exercise);
          const done = toBoolean(exercise.is_done);
          const isValidating = validatingKeys.includes(exerciseKey);

          return (
            <article
              key={exerciseKey}
              className={`overflow-hidden rounded-[2rem] border shadow-lg transition ${
                done
                  ? "border-emerald-200 bg-emerald-50 shadow-emerald-500/10 dark:border-emerald-500/20 dark:bg-emerald-500/10"
                  : "border-slate-200 bg-slate-50 shadow-slate-200/50 dark:border-white/10 dark:bg-white/[0.04] dark:shadow-black/20"
              }`}
            >
              <div className="flex flex-col sm:flex-row">
                <div className="flex h-44 items-center justify-center overflow-hidden bg-white dark:bg-slate-950 sm:h-auto sm:w-44">
                  {exercise.lien_photo ? (
                    <img
                      src={exercise.lien_photo}
                      alt={exercise.nom}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <FaDumbbell className="text-4xl text-slate-400" />
                  )}
                </div>

                <div className="flex flex-1 flex-col p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold ${
                        done
                          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200"
                          : "bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-200"
                      }`}
                    >
                      {done ? <FaCheckCircle /> : <FaClock />}
                      {done ? "Réalisé" : "À faire"}
                    </span>

                    <span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-slate-500 shadow-sm dark:bg-white/10 dark:text-white/50">
                      {formatDateFr(exercise.date_realisation)}
                    </span>
                  </div>

                  <h3 className="mt-4 font-titre text-2xl font-bold text-slate-950 dark:text-white">
                    {exercise.nom}
                  </h3>

                  <p className="mt-2 text-sm font-medium leading-7 text-slate-500 dark:text-white/50">
                    {exercise.description || "Aucune description renseignée."}
                  </p>

                  <div className="mt-4 rounded-2xl bg-white p-3 text-sm font-bold text-emerald-700 shadow-sm dark:bg-white/10 dark:text-emerald-200">
                    Objectif :{" "}
                    {exercise.repetitions
                      ? `${exercise.repetitions} répétition(s)`
                      : exercise.programme || "Non renseigné"}
                  </div>

                  {exercise.lien_video && (
                    <a
                      href={exercise.lien_video}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-sky-700 hover:text-sky-900 dark:text-sky-200"
                    >
                      <FaExternalLinkAlt />
                      Voir la vidéo
                    </a>
                  )}

                  <div className="mt-5">
                    <button
                      type="button"
                      onClick={() => onValidate(exercise)}
                      disabled={done || isValidating}
                      className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500 dark:disabled:bg-white/10 dark:disabled:text-white/40"
                    >
                      <FaCheck />
                      {done
                        ? "Déjà validé"
                        : isValidating
                          ? "Validation..."
                          : "J'ai réalisé l'exercice"}
                    </button>
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

export default AdherentExerciseList;